/**
 * Serviço para gerenciar workspaces
 */

import { getToken } from "./tokenService";
import {
  Workspace,
  WorkspacesListResponse,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  WorkspacesStats,
  WorkspaceStatusType,
} from "@/types/workspace";

import { API_BASE_URL } from "@/lib/apiConfig";

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: string;
  };
  message?: string;
}

/**
 * Faz uma requisição autenticada para a API
 */
async function apiRequest<T>(
  endpoint: string,
  method: string = "GET",
  body?: any
): Promise<ApiResponse<T>> {
  const token = getToken();

  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || {
          code: "UNKNOWN_ERROR",
          message: "Erro desconhecido",
        },
      };
    }

    return data;
  } catch (error) {
    return {
      success: false,
      error: {
        code: "NETWORK_ERROR",
        message: "Erro de conexão com o servidor",
        details: error instanceof Error ? error.message : undefined,
      },
    };
  }
}

/**
 * Lista todos os workspaces
 */
export async function listWorkspaces(params?: {
  limit?: number;
  offset?: number;
  status_id?: number;
  client_id?: string;
  search?: string;
}): Promise<ApiResponse<WorkspacesListResponse>> {
  const queryParams = new URLSearchParams();

  if (params?.limit) queryParams.append("limit", params.limit.toString());
  if (params?.offset) queryParams.append("offset", params.offset.toString());
  if (params?.status_id)
    queryParams.append("status_id", params.status_id.toString());
  if (params?.client_id) queryParams.append("client_id", params.client_id);
  if (params?.search) queryParams.append("search", params.search);

  const query = queryParams.toString();
  const endpoint = `/workspaces${query ? `?${query}` : ""}`;

  return apiRequest<WorkspacesListResponse>(endpoint);
}

/**
 * Obtém um workspace por ID
 */
export async function getWorkspaceById(
  id: string
): Promise<ApiResponse<Workspace>> {
  return apiRequest<Workspace>(`/workspaces/${id}`);
}

/**
 * Cria um novo workspace
 */
export async function createWorkspace(
  workspace: CreateWorkspaceRequest
): Promise<ApiResponse<Workspace>> {
  return apiRequest<Workspace>("/workspaces", "POST", workspace);
}

/**
 * Atualiza um workspace
 */
export async function updateWorkspace(
  id: string,
  workspace: UpdateWorkspaceRequest
): Promise<ApiResponse<Workspace>> {
  return apiRequest<Workspace>(`/workspaces/${id}`, "PUT", workspace);
}

/**
 * Remove um workspace
 */
export async function deleteWorkspace(id: string): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/workspaces/${id}`, "DELETE");
}

/**
 * Altera o status de um workspace
 */
export async function changeWorkspaceStatus(
  id: string,
  status_id: number
): Promise<ApiResponse<Workspace>> {
  return apiRequest<Workspace>(`/workspaces/${id}/status`, "PATCH", {
    status_id,
  });
}

/**
 * Associa um usuário a um workspace
 */
export async function associateUserToWorkspace(
  workspaceId: string,
  userId: string
): Promise<ApiResponse<void>> {
  return apiRequest<void>(`/workspaces/${workspaceId}/users`, "POST", {
    user_id: userId,
  });
}

/**
 * Remove a associação de um usuário de um workspace
 */
export async function dissociateUserFromWorkspace(
  workspaceId: string,
  userId: string
): Promise<ApiResponse<void>> {
  return apiRequest<void>(
    `/workspaces/${workspaceId}/users/${userId}`,
    "DELETE"
  );
}

/**
 * Obtém estatísticas dos workspaces
 */
export async function getWorkspacesStats(): Promise<
  ApiResponse<WorkspacesStats>
> {
  return apiRequest<WorkspacesStats>("/workspaces/stats");
}

/**
 * Lista tipos de status de workspace
 */
export async function listWorkspaceStatusTypes(): Promise<
  ApiResponse<WorkspaceStatusType[]>
> {
  return apiRequest<WorkspaceStatusType[]>("/workspaces/status-types");
}

// Re-export types
export type {
  Workspace,
  WorkspacesListResponse,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  WorkspacesStats,
  WorkspaceStatusType,
};

